import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { ConfirmDialog } from '../components/confirm-dialog.js';
import { Mag } from '../components/mag.js';
import { magToast } from '../components/toaster.js';
import { TopNav } from '../components/top-nav.js';
import {
  type CampaignKind,
  type CampaignSummary,
  deleteCampaign,
  type fetchCampaigns,
  type fetchMe,
  pauseCampaign,
  resumeCampaign,
} from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { brandsQuery, campaignsQuery, meQuery } from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';
import { type ServingState, servingStateFromStatusMap } from '../lib/serving-state.js';

// /brand/$slug/campaigns — TRIGGER + LIST. Per handoff §4.1 the trigger
// is *not* a separate route; it lives at the top of the Campaigns page.
// Below the trigger grid: existing campaigns. The "talk to mag" card is
// dominant (dark ink bg + plum drop-shadow); clone/import are secondary.
//
// This is the index child of brand.$slug.campaigns.tsx — the parent is a
// thin layout (Outlet only) so sub-routes like /campaigns/new/brief have
// somewhere to render.

// URL-driven filters so a refresh + a bookmarked link survive. The
// tab partitions by life-cycle bucket; the kind chip is a secondary
// filter on top. Both default to 'all' = unfiltered.
const TAB_VALUES = ['all', 'drafts', 'needs_you', 'live'] as const;
type TabValue = (typeof TAB_VALUES)[number];
const KIND_FILTER_VALUES = ['all', 'ads', 'instagram', 'analytics', 'funnel'] as const;
type KindFilterValue = (typeof KIND_FILTER_VALUES)[number];

const campaignsSearchSchema = z.object({
  tab: z.enum(TAB_VALUES).optional(),
  kind: z.enum(KIND_FILTER_VALUES).optional(),
});

export const Route = createFileRoute('/brand/$slug/campaigns/')({
  validateSearch: campaignsSearchSchema,
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    // Unification Phase 3 — campaignsQuery now returns both briefed
    // and imported rows (origin=all on the fetch), so no separate
    // imported pre-warm is needed.
    const list = await queryClient.ensureQueryData(campaignsQuery(brand.id));
    return { me, list, brand };
  },
  loader: ({ context }) =>
    context as {
      me: Awaited<ReturnType<typeof fetchMe>>;
      list: Awaited<ReturnType<typeof fetchCampaigns>>;
      brand: { id: string; name: string; slug: string };
    },
  component: CampaignsPage,
});

// Which life-cycle bucket the campaign sits in. A campaign maps to
// at most one tab — the tab list is mutually exclusive so counts add
// up to the visible total.
/** Exported so the bucket rules can be asserted without mounting the route —
 *  the same reason watchingRowState is exported from the rail. */
/** Which platform chip a row leads with — google's word, not ours.
 *
 *  PURE AND EXPORTED because the first cut of this rule was inline and
 *  unasserted, and it shipped wrong: it rendered the chip only for the
 *  EXCEPTIONAL states and let the healthy one fall through, so a campaign
 *  google reported as serving read "BARELY SHOWING" on the list and "LIVE"
 *  on its own page. The header's rule is the contract; this is it, in one
 *  testable place.
 *
 *  'serving' and 'unknown' BOTH map to live, which is deliberate and is what
 *  the header does: LIVE is the status pill, shown whenever the campaign is
 *  not paused, pending or limited. On its own that would overclaim about a
 *  campaign we have no fresh reading for — it does not here, because the
 *  measured verdict chip renders beside it (decision 909a74f). */
export type PlatformChipKind = 'in_review' | 'limited' | 'live' | null;

export function platformChipKind(
  status: CampaignSummary['status'],
  isPaused: boolean,
  serving: ServingState,
): PlatformChipKind {
  if (status !== 'launched' || isPaused) return null;
  if (serving === 'pending') return 'in_review';
  if (serving === 'limited') return 'limited';
  return 'live';
}

export function tabFor(c: CampaignSummary): TabValue | null {
  if (c.status === 'intake' || c.status === 'drafting') return 'drafts';
  if (c.status === 'ready' && !c.captured) return 'needs_you';
  // A LAUNCHED CAMPAIGN CAN ALSO NEED YOU, and until now it could not say so.
  // needs_you meant only "drafted and waiting to be sent", so a live campaign
  // whose panel read NEEDS A DECISION — seven keywords google will never
  // serve — sat in LIVE while the tab above it read NEEDS YOU 0.
  //
  // Placed BEFORE the launched check because the tabs are mutually exclusive
  // and this is the more actionable of the two truths: "it is running" is not
  // news, "it is running and needs you" is.
  if (c.status === 'launched' && c.needsDecision) return 'needs_you';
  // [spec 3a365634] ...and so can a campaign mag has filed an open ask
  // about, at any status. The founder reported the feed saying NEEDS YOU
  // 2 while this tab said 0 — two true numbers counting different things.
  //
  // OR, never a sum. A campaign with a dead keyword AND two cards about
  // it is one campaign that needs you, not three; the tab counts
  // campaigns and the chip on the row carries how many.
  //
  // Not gated on 'launched', unlike the line above: a draft can have an
  // ask filed against it too, and gating would move the blind spot one
  // status along rather than closing it.
  if ((c.attentionCount ?? 0) > 0) return 'needs_you';
  if (c.status === 'launched') return 'live';
  // failed + captured don't get a primary tab — they show only on
  // 'all'. Keeps the live tabs focused on the work the founder owns.
  return null;
}

const KIND_TO_FILTER: Record<CampaignKind, KindFilterValue | null> = {
  creative_ads: 'ads',
  organic_meta: 'instagram',
  analytics_setup: 'analytics',
  conversion_path: 'funnel',
  unclassified: null,
  // Imported takeovers map to the same "ads" filter chip — they ARE
  // paid ads, just provenance differs (Phase 3 of the unification
  // adds a separate origin chip).
  imported_paid: 'ads',
};

function matchesKindFilter(c: CampaignSummary, kind: KindFilterValue): boolean {
  if (kind === 'all') return true;
  return KIND_TO_FILTER[c.kind] === kind;
}

function matchesTab(c: CampaignSummary, tab: TabValue): boolean {
  if (tab === 'all') return true;
  return tabFor(c) === tab;
}

function CampaignsPage() {
  const { t } = useLingui();
  const { list, brand } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const tab: TabValue = search.tab ?? 'all';
  const kindFilter: KindFilterValue = search.kind ?? 'all';
  // Unification roadmap Phase 3 — `campaigns` now carries both briefed
  // and imported rows (api returns origin=all). The dedicated
  // importedCampaignsQuery and its filter dance are gone; the unified
  // list renders both with an origin chip distinguishing imports.
  const campaigns = list?.campaigns ?? [];
  const hasCampaigns = campaigns.length > 0;
  useBrandTitle(brand.slug, [t`campaigns`]);

  // Tab counts ignore the active kind filter (so the user can see
  // "if i switch tabs how many would i find"); kind counts respect
  // the active tab (chips read against the current bucket).
  const tabCounts: Record<TabValue, number> = {
    all: campaigns.length,
    drafts: 0,
    needs_you: 0,
    live: 0,
  };
  for (const c of campaigns) {
    const t = tabFor(c);
    if (t) tabCounts[t]++;
  }
  const visibleByTab = campaigns.filter((c) => matchesTab(c, tab));
  const kindCounts: Record<KindFilterValue, number> = {
    all: visibleByTab.length,
    ads: 0,
    instagram: 0,
    analytics: 0,
    funnel: 0,
  };
  for (const c of visibleByTab) {
    const k = KIND_TO_FILTER[c.kind];
    if (k) kindCounts[k]++;
  }
  const visible = visibleByTab.filter((c) => matchesKindFilter(c, kindFilter));

  const setTab = (next: TabValue) => {
    navigate({
      search: (s) => ({
        ...s,
        tab: next === 'all' ? undefined : next,
      }),
    });
  };
  const setKindFilter = (next: KindFilterValue) => {
    navigate({
      search: (s) => ({
        ...s,
        kind: next === 'all' ? undefined : next,
      }),
    });
  };

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <TopNav active="campaigns" />

      <section className="mx-auto max-w-[1120px] px-4 pb-16 pt-8 sm:px-7">
        {hasCampaigns ? (
          // Has campaigns — the list is the page. Create options
          // collapse into a compact action row on the right of the
          // header. Founder's primary task here is reading what they
          // already have, not starting something new.
          <>
            <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
              <div>
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
                  {'↘'} <Trans>campaigns</Trans>
                </p>
                <h1 className="mt-2 font-display text-[28px] sm:text-[38px] font-black leading-[1.05] tracking-[-0.04em]">
                  <Trans>your campaigns.</Trans>
                </h1>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-3">
                <Link
                  to="/brand/$slug/campaigns/import"
                  params={{ slug: brand.slug }}
                  className="link-quiet text-[12px]"
                >
                  {'↘'} <Trans>take one over</Trans>
                </Link>
                <Link
                  to="/brand/$slug/campaigns/new/brief"
                  params={{ slug: brand.slug }}
                  className="btn"
                >
                  {'+'} <Trans>brief a new one</Trans>
                  <span className="btn-arrow">→</span>
                </Link>
              </div>
            </div>
            {/* Unification roadmap Phase 3 — imported campaigns are
                now in `campaigns` (origin='imported') and surface in
                the unified list with an origin chip on each row.
                Previously they had a dedicated "↘ imported" section
                above the briefed list, shipped in [12.2L-81]; that's
                gone now that one list serves both.
                ImportedCampaignsSection + importedForBrand are dead
                code post-Phase 3 and get removed in a follow-up cleanup. */}
            {campaigns.length > 0 ? (
              <>
                <FilterStrip
                  tab={tab}
                  kind={kindFilter}
                  tabCounts={tabCounts}
                  kindCounts={kindCounts}
                  onTabChange={setTab}
                  onKindChange={setKindFilter}
                />
                {visible.length > 0 ? (
                  <CampaignList campaigns={visible} slug={brand.slug} />
                ) : (
                  <FilterEmptyState
                    tab={tab}
                    kind={kindFilter}
                    onReset={() => navigate({ search: () => ({}) })}
                  />
                )}
              </>
            ) : null}
          </>
        ) : (
          // First-time / empty — hero invites the founder to brief
          // their first campaign. The three create-method cards
          // explain the options inline since there's nothing else on
          // the page to compete with them.
          <>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
              {'↘'} <Trans>campaigns</Trans>
            </p>
            <h1 className="mt-2 font-display text-[28px] sm:text-[38px] font-black leading-[1.05] tracking-[-0.04em]">
              <Trans>brief a new one.</Trans>
            </h1>
            <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-[1.4fr_1fr]">
              <TalkToMagCard slug={brand.slug} />
              <ImportCard slug={brand.slug} />
            </div>
            <div className="mt-12">
              <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
                {'↘'} <Trans>your campaigns</Trans>
              </p>
              <EmptyState />
            </div>
          </>
        )}
      </section>
    </main>
  );
}

function TalkToMagCard({ slug }: { slug: string }) {
  // Workbench-family chrome: white surface, ink border + plum drop
  // shadow, plum accents. Earlier ink-bg + spell-yellow-text
  // treatment read as too loud against the cream page — the
  // RECOMMENDED pill + the cat + Mag's voice quote already do the
  // "this is what I'd do" work without needing a fully inverted card.
  return (
    <article className="relative overflow-hidden rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-6 shadow-[4px_4px_0_var(--plum)]">
      <div className="flex items-center gap-3">
        <Mag size={48} mood="casting" />
        <span className="inline-flex items-center rounded-full border border-[var(--plum)]/35 bg-[var(--plum)]/8 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--plum)]">
          <Trans>RECOMMENDED</Trans>
        </span>
      </div>
      <p className="mag-voice mt-4 font-serif text-[22px] leading-tight text-[var(--ink)]">
        <Trans>“tell me what you're trying to do. i'll handle the rest.”</Trans>
      </p>
      <p className="mt-3 text-[14px] leading-[1.5] text-[var(--ink)]/70">
        <Trans>
          write me one paragraph. i'll draft the audience, the budget, six creative variants, and
          ship it for your review.
        </Trans>
      </p>
      <div className="mt-5 flex justify-end">
        <Link to="/brand/$slug/campaigns/new/brief" params={{ slug }} className="btn btn-spell">
          <Trans>brief mag · recommended</Trans>
          <span className="btn-arrow">→</span>
        </Link>
      </div>
    </article>
  );
}

function ImportCard({ slug }: { slug: string }) {
  return (
    <article className="flex flex-col rounded-2xl border border-[var(--line)] bg-white p-5">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
        {'↘'} <Trans>import</Trans>
      </p>
      <h3 className="mt-2 font-display text-[20px] font-extrabold leading-tight tracking-[-0.02em]">
        <Trans>take over an existing one.</Trans>
      </h3>
      <p className="mt-2 flex-1 text-[13px] leading-[1.5] text-[var(--ink)]/70">
        <Trans>hand me a live google ads campaign and i'll read it before changing anything.</Trans>
      </p>
      <div className="mt-3 flex justify-end">
        <Link
          to="/brand/$slug/campaigns/import"
          params={{ slug }}
          className="btn btn-ghost text-[11px]"
        >
          <Trans>take one over</Trans>
          <span className="btn-arrow">→</span>
        </Link>
      </div>
    </article>
  );
}

function EmptyState() {
  return (
    <div className="mt-4 rounded-2xl border border-dashed border-[var(--line-strong)] bg-[var(--paper)] p-8 text-center">
      <Mag size={56} mood="napping" />
      <p className="mt-3 font-serif text-lg leading-tight text-[var(--ink)]/80">
        <Trans>“no campaigns yet. brief me and we'll fill this in.”</Trans>
      </p>
    </div>
  );
}

// ─── Filter strip ─────────────────────────────────────────────────
// Tabs partition by life-cycle bucket (drafts / needs you / live);
// kind chips filter on top. Counts beside each label show how many
// campaigns sit in that bucket so the founder picks an action with
// signal rather than a blind click.

/** The `t` that useLingui() hands back. Named once so the label
 *  builders below can take it without repeating the lookup. */
type T = ReturnType<typeof useLingui>['t'];

// These three were module-level const maps. A module-level t`` evaluates
// before any locale is active and bakes English in for the life of the
// process, so each is now built during render from the caller's `t`.
// The KEYS stay as they are — they are filter values and URL state, not
// copy, and must not move with the language.
function tabLabels(t: T): Record<TabValue, string> {
  return {
    all: i18n._(msg`all`),
    drafts: i18n._(msg`drafts`),
    needs_you: i18n._(msg`needs you`),
    live: i18n._(msg`live`),
  };
}

function kindFilterLabels(t: T): Record<KindFilterValue, string> {
  return {
    all: i18n._(msg`all kinds`),
    ads: i18n._(msg`ads`),
    instagram: i18n._(msg`instagram`),
    analytics: i18n._(msg`analytics`),
    funnel: i18n._(msg`funnel`),
  };
}

function FilterStrip({
  tab,
  kind,
  tabCounts,
  kindCounts,
  onTabChange,
  onKindChange,
}: {
  tab: TabValue;
  kind: KindFilterValue;
  tabCounts: Record<TabValue, number>;
  kindCounts: Record<KindFilterValue, number>;
  onTabChange: (next: TabValue) => void;
  onKindChange: (next: KindFilterValue) => void;
}) {
  // Hide kind chips that have zero matches under the current tab —
  // keeps the strip from cluttering with "instagram (0)" when the
  // founder is on the "live" tab and has only ads live.
  const { t } = useLingui();
  const labels = tabLabels(t);
  const kindLabels = kindFilterLabels(t);
  const visibleKindChips = (KIND_FILTER_VALUES as readonly KindFilterValue[]).filter(
    (k) => k === 'all' || k === kind || kindCounts[k] > 0,
  );
  return (
    <div className="mt-6 flex flex-col gap-3 border-b border-[var(--line)] pb-4">
      <div className="-mb-px flex flex-wrap items-baseline gap-x-5 gap-y-2">
        {TAB_VALUES.map((tabValue) => {
          const active = tabValue === tab;
          return (
            <button
              key={tabValue}
              type="button"
              onClick={() => onTabChange(tabValue)}
              className={`relative pb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors ${
                active ? 'text-[var(--ink)]' : 'text-[var(--ink)]/45 hover:text-[var(--ink)]/75'
              }`}
            >
              {labels[tabValue]}
              <span
                className={`ml-1.5 tabular-nums ${
                  active ? 'text-[var(--ink)]/65' : 'text-[var(--ink)]/35'
                }`}
              >
                {tabCounts[tabValue]}
              </span>
              {active ? (
                <span className="-bottom-px absolute right-0 left-0 h-[2px] bg-[var(--ink)]" />
              ) : null}
            </button>
          );
        })}
      </div>
      {visibleKindChips.length > 1 ? (
        <div className="flex flex-wrap items-center gap-2">
          {visibleKindChips.map((k) => {
            const active = k === kind;
            return (
              <button
                key={k}
                type="button"
                onClick={() => onKindChange(k)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.1em] transition ${
                  active
                    ? 'border-[var(--ink)] bg-[var(--ink)] text-[var(--cream)]'
                    : 'border-[var(--ink)]/15 bg-[var(--paper)] text-[var(--ink)]/75 hover:border-[var(--ink)]/35'
                }`}
              >
                {kindLabels[k]}
                <span
                  className={`tabular-nums ${
                    active ? 'text-[var(--cream)]/75' : 'text-[var(--ink)]/45'
                  }`}
                >
                  {kindCounts[k]}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function FilterEmptyState({
  tab,
  kind,
  onReset,
}: {
  tab: TabValue;
  kind: KindFilterValue;
  onReset: () => void;
}) {
  const { t } = useLingui();
  // Four whole sentences rather than one assembled from fragments: the
  // label lands mid-sentence, and a language that orders those parts
  // differently cannot fix a template it does not own.
  const filterDescription = (() => {
    const tabName = tabLabels(t)[tab];
    const kindName = kindFilterLabels(t)[kind];
    if (tab !== 'all' && kind !== 'all') {
      return t`no ${kindName} campaigns in ${tabName} right now.`;
    }
    if (tab !== 'all') return t`no campaigns in ${tabName} right now.`;
    if (kind !== 'all') return t`no ${kindName} campaigns yet.`;
    return t`no campaigns yet.`;
  })();
  return (
    <div className="mt-6 rounded-2xl border border-dashed border-[var(--line-strong)] bg-[var(--paper)] p-8 text-center">
      <Mag size={48} mood="napping" />
      <p className="mt-2 font-serif text-[15px] leading-tight text-[var(--ink)]/80">
        {filterDescription}
      </p>
      {tab !== 'all' || kind !== 'all' ? (
        <button
          type="button"
          onClick={onReset}
          className="mt-3 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--ink)]/55 underline-offset-4 hover:text-[var(--ink)] hover:underline"
        >
          <Trans>clear filters</Trans>
        </button>
      ) : null}
    </div>
  );
}

function CampaignList({ campaigns, slug }: { campaigns: CampaignSummary[]; slug: string }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const router = useRouter();
  // Shared delete state — one dialog at a time so we don't render N
  // ConfirmDialogs in the DOM.
  const [pendingDelete, setPendingDelete] = useState<CampaignSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [pausingId, setPausingId] = useState<string | null>(null);

  // Pause and resume are NOT behind a confirm dialog, unlike delete. Both are
  // reversible from the same button, and a dialog in front of "stop spending
  // my money" is friction in the wrong direction.
  const togglePause = async (campaign: CampaignSummary) => {
    setPausingId(campaign.id);
    try {
      if (campaign.status === 'paused') {
        const out = await resumeCampaign(campaign.id);
        if (out.stillPaused.length > 0) {
          magToast.error(
            t`resumed ${out.resumedCount}, but not all of them.`,
            t`${out.stillPaused.length} ad(s) wouldn't restart, so the campaign is still paused.`,
          );
        } else {
          magToast.success(t`back on.`, t`the ads are running and mag is watching again.`);
        }
      } else {
        const out = await pauseCampaign(campaign.id);
        // campaignPaused is the honest bit. A partial pause really stopped
        // some ads, so this is not an error — but the campaign is still live
        // and mag is still reading it, and saying otherwise would be the lie
        // this whole surface exists to avoid.
        if (out.campaignPaused) {
          magToast.success(
            t`paused.`,
            t`the ads are stopped and mag has stepped back from this one.`,
          );
        } else {
          magToast.error(
            t`paused ${out.pausedCount}, but not all of them.`,
            t`${out.stillRunning.length} ad(s) are still running, so the campaign is still live and mag is still watching.`,
          );
        }
      }
      await refreshQueries(queryClient, ['campaigns']);
      await router.invalidate();
    } catch (err) {
      magToast.error("mag couldn't do that.", founderErrorCopy(err, 'try again.'));
    } finally {
      setPausingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteCampaign(pendingDelete.id);
      // Mutation refresh pattern (a CommitMind rule): refetch, don't just invalidate,
      // because ensureQueryData reads in beforeLoad don't subscribe.
      await refreshQueries(queryClient, ['campaigns']);
      await router.invalidate();
      magToast.success(t`campaign deleted.`, t`the ads are stopped and it's out of the list.`);
      setPendingDelete(null);
    } catch (err) {
      // The api refuses rather than half-succeeding: if an ad could not
      // be stopped on its platform, NOTHING was archived and the
      // campaign is still live. Say that, because telling the founder a
      // spending campaign is gone is the exact bug this path had.
      magToast.error(
        t`mag couldn't stop those ads — nothing was deleted.`,
        founderErrorCopy(err, 'the campaign is still running. try again in a moment.'),
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <ul className="mt-6 flex flex-col gap-2">
        {campaigns.map((c) => (
          <li key={c.id}>
            <CampaignRow
              campaign={c}
              slug={slug}
              onAskDelete={() => setPendingDelete(c)}
              onAskPause={() => void togglePause(c)}
              busy={pausingId === c.id}
            />
          </li>
        ))}
      </ul>
      <ConfirmDialog
        open={pendingDelete !== null}
        title={t`delete this campaign?`}
        body={
          <Trans>
            this stops <strong>{pendingDelete?.name ?? pendingDelete?.brief.slice(0, 60)}</strong>{' '}
            on google and meta for good — the ads are removed, not paused, and can't be put back.
            mag keeps the record so you can still see what ran and what it cost.
          </Trans>
        }
        confirmLabel={t`stop the ads + delete`}
        destructive
        busy={deleting}
        busyLabel={t`deleting…`}
        onConfirm={confirmDelete}
        onCancel={() => (deleting ? undefined : setPendingDelete(null))}
      />
    </>
  );
}

// Status pills carry a short "core" label that always shows and an
// optional "suffix" that we hide on mobile so the row doesn't push the
// title against the edge. e.g. on a phone "READY · NEEDS YOU" reads as
// "READY" — still scannable, less crowded.
type Pill = { label: string; suffix?: string; bg: string; fg: string };

function statusPills(t: T): Record<CampaignSummary['status'], Pill> {
  return {
    intake: { label: i18n._(msg`BRIEFING`), bg: 'var(--paper)', fg: 'var(--ink)' },
    drafting: { label: i18n._(msg`DRAFTING`), bg: 'var(--paper)', fg: 'var(--ink)' },
    ready: {
      label: i18n._(msg`READY`),
      suffix: i18n._(msg`· NEEDS YOU`),
      bg: 'var(--plum)',
      fg: 'var(--cream)',
    },
    launched: { label: i18n._(msg`LIVE`), bg: 'var(--spell)', fg: 'var(--ink)' },
    failed: { label: i18n._(msg`FAILED`), bg: 'var(--pulse)', fg: 'var(--cream)' },
    // Both of these became reachable only once something wrote them —
    // 'paused' from campaign-level pause, 'archived' from delete, which
    // archives rather than hard-deleting. The map is exhaustive over the api
    // enum, so widening that enum is what forced these entries to exist
    // rather than a status quietly rendering as blank.
    paused: { label: i18n._(msg`PAUSED`), bg: 'var(--paper)', fg: 'var(--ink)' },
    archived: { label: i18n._(msg`DELETED`), bg: 'var(--paper)', fg: 'var(--ink)' },
  };
}

/** The list's serving badges, worded to match the detail page's
 *  verdict chips rather than inventing a second vocabulary — two
 *  screens disagreeing about one campaign is the bug being fixed, and
 *  agreeing in state but not in words would be a quieter version of
 *  it.
 *
 *  `readable` returns the ordinary LIVE pill: it is the healthy state,
 *  and a special badge for "working normally" is noise. */
function verdictPills(t: T): Record<NonNullable<CampaignSummary['verdictState']>, Pill> {
  return {
    not_serving: { label: i18n._(msg`NOT SERVING`), bg: 'var(--pulse)', fg: 'var(--cream)' },
    serving_thin: { label: i18n._(msg`BARELY SHOWING`), bg: 'var(--paper)', fg: 'var(--ink)' },
    pending: { label: i18n._(msg`JUST LAUNCHED`), bg: 'var(--paper)', fg: 'var(--ink)' },
    learning: { label: i18n._(msg`LEARNING`), bg: 'var(--paper)', fg: 'var(--ink)' },
    readable: { label: i18n._(msg`LIVE`), bg: 'var(--spell)', fg: 'var(--ink)' },
  };
}

// s156 — when the founder has paused Mag on a launched campaign, the
// row's pill flips from LIVE → PAUSED so the campaigns list reads
// "this one's stopped" at a glance without opening the detail page.
// Paper bg + full ink reads as a quieter "set aside" state vs LIVE's
// spell-yellow "active" energy.
function pausedPill(t: T): Pill {
  return { label: i18n._(msg`PAUSED`), bg: 'var(--paper)', fg: 'var(--ink)' };
}

// "Captured" pill — non-creative campaigns that got classified but have
// no real pipeline yet. They live in status='ready' with draft=null;
// distinct from "ready · needs you" which means a real draft is sitting
// there waiting on the founder.
function capturedPill(t: T): Pill {
  return {
    label: i18n._(msg`CAPTURED`),
    suffix: i18n._(msg`· BUILDING NEXT`),
    bg: 'var(--paper)',
    fg: 'var(--ink)',
  };
}

// Quick visual taxonomy chip on the left of each row. Lets the founder
// scan ANALYTICS vs ADS vs FUNNEL at a glance without reading the
// title. Unclassified campaigns (mid-intake, no kind yet) skip the
// chip to avoid a meaningless "—" placeholder.
function kindLabels(t: T): Record<CampaignSummary['kind'], string | null> {
  return {
    analytics_setup: i18n._(msg`ANALYTICS`),
    creative_ads: i18n._(msg`ADS`),
    conversion_path: i18n._(msg`FUNNEL`),
    organic_meta: i18n._(msg`INSTAGRAM`),
    unclassified: null,
    // Imported paid ads keep an ADS label — the origin chip (added
    // in Phase 3 of the unification) distinguishes provenance.
    imported_paid: i18n._(msg`ADS`),
  };
}

function CampaignRow({
  campaign,
  slug,
  onAskDelete,
  onAskPause,
  busy,
}: {
  campaign: CampaignSummary;
  slug: string;
  onAskDelete: () => void;
  onAskPause: () => void;
  /** A pause or resume is in flight for THIS row. */
  busy: boolean;
}) {
  // s156 — paused state takes precedence over the captured / launched
  // pill: it's the more important *current* signal for the founder
  // (campaign exists but Mag isn't doing anything on it).
  //
  // Two different things stop: the analytics read cadence, and paid
  // delivery. scanCadence='off' was the only one this pill knew about,
  // so a campaign whose every ad mag had auto-paused still wore the
  // LIVE pill — the same lie the home rail told. paidDelivery carries
  // the second (null = no paid deliverables, NOT "nothing running").
  const paidStopped = campaign.paidDelivery !== null && campaign.paidDelivery.running === 0;
  // status==='paused' comes FIRST, and that clause is new because nothing used
  // to write it: the enum has carried 'paused' since it was defined, and the
  // api never set it. This condition was therefore built entirely out of
  // derived signals (cadence off, or every ad stopped) under a
  // status==='launched' guard — which would have excluded the very state a
  // campaign-level pause now produces.
  const isPaused =
    campaign.status === 'paused' ||
    (campaign.status === 'launched' && (campaign.scanCadence === 'off' || paidStopped));

  // The control is offered only where it can do something: a campaign with
  // real platform launches behind it. paidDelivery is null when there are
  // none (mock launches included — derivePaidDeliveryFromDraft filters on
  // launched_via), which is exactly the set the api refuses with
  // 'nothing_running'. Showing a button that can only error is worse than
  // showing none.
  const canPause = campaign.paidDelivery !== null;
  const paused = campaign.status === 'paused';
  const { t } = useLingui();
  // THE SERVING VERDICT OUTRANKS THE STATUS PILL on a launched
  // campaign (spec be02dc43). `status` is 'launched' whatever google
  // says, so badging off it told the founder LIVE about a campaign the
  // detail page called LIMITED, on the same minute. Same shape
  // paidDelivery already fixed for pauses.
  //
  // Paused still wins over it: a campaign mag has stopped is not
  // serving for a reason the founder can act on directly, and that is
  // the more useful thing to say.
  //
  // Absent verdict falls through to the old pill, never to live-by-
  // default — a campaign we could not read is not a campaign we know
  // is running.
  const servingPill =
    campaign.status === 'launched' && !isPaused && campaign.verdictState
      ? verdictPills(t)[campaign.verdictState]
      : null;

  // GOOGLE'S OWN WORD, beside ours (decision 909a74f). The founder asked for
  // the LIVE/LIMITED chip back on the list after the verdict replaced it, and
  // chose — knowing f55aa4d7 measured primary_status as unable to tell a dead
  // campaign from a working one — to see both signals rather than have the
  // verdict speak for the platform.
  //
  // They CAN disagree, and that is the accepted cost. What must NOT happen is
  // the chip itself differing between this row and the detail header, so both
  // derive from the same rules in lib/serving-state.ts; only the input shape
  // differs (the list has readings, the detail has deliverables).
  const platformServing = servingStateFromStatusMap(campaign.platformServingStatus);
  //
  // MIRRORS THE DETAIL HEADER EXACTLY, including the ordinary LIVE case.
  // An earlier cut rendered this chip only when it said something
  // EXCEPTIONAL (in review / limited) and let the healthy case fall through
  // to the verdict alone — so a campaign google reports as serving showed
  // "BARELY SHOWING" on the list and "LIVE" on its own page. Same two
  // screens, same minute, different answers: the defect this task exists to
  // end, reintroduced by the fix for it.
  //
  // LIVE here is the STATUS pill, exactly as the header uses it — which is
  // why it also shows when the reading is absent or stale. That would be an
  // overclaim on its own, and is not one here: the verdict chip rides beside
  // it, so the founder reads "google says live, we measured barely showing"
  // rather than just "live".
  const chipKind = platformChipKind(campaign.status, isPaused, platformServing);
  const platformPill: Pill | null =
    chipKind === 'in_review'
      ? // Review is the normal path for a new ad and usually clears within a
        // day. Not an error colour — decision 8b0735a5.
        { label: t`IN REVIEW`, bg: 'var(--plum)', fg: 'var(--cream)' }
      : chipKind === 'limited'
        ? { label: t`LIMITED`, bg: 'var(--plum)', fg: 'var(--cream)' }
        : chipKind === 'live'
          ? statusPills(t).launched
          : null;

  const basePill = isPaused
    ? pausedPill(t)
    : (platformPill ??
      servingPill ??
      (campaign.captured ? capturedPill(t) : statusPills(t)[campaign.status]));
  // "· NEEDS YOU" rides the lead chip rather than becoming a third one — the
  // row is already carrying two, and this is the same suffix mechanism the
  // READY pill uses, so the vocabulary is one the founder has already learned.
  //
  // A launched campaign with a dead keyword now SAYS so here, instead of only
  // inside the panel two clicks away.
  const pill: Pill =
    campaign.needsDecision && !isPaused ? { ...basePill, suffix: t`· NEEDS YOU` } : basePill;
  // The second chip. Only rendered when it ADDS something: when the platform
  // chip took the lead slot, the verdict follows it, so the row reads
  // "[LIMITED] [LEARNING]" — google's report, then what we measured.
  const secondPill = platformPill && servingPill ? servingPill : null;
  const link = nextDestination(campaign);
  const kindLabel = kindLabels(t)[campaign.kind];
  // Unification roadmap Phase 3 — origin chip on imported rows so the
  // founder reads "this came from my ad account" at a glance, distinct
  // from a mag-drafted briefed campaign. Briefed rows get no chip —
  // that's the default mental model.
  const originChip =
    campaign.origin === 'imported' ? (
      <span className="inline-flex shrink-0 items-center rounded-full border border-[var(--plum)]/40 bg-[var(--plum)]/8 px-2.5 py-1 font-mono text-[9.5px] font-bold uppercase tracking-[0.14em] text-[var(--plum)]">
        {'↘'} <Trans>imported</Trans>
        {campaign.provider ? ` · ${campaign.provider}` : ''}
      </span>
    ) : null;
  // [12.2L-96] At-a-glance signal that mag is acting autonomously on
  // this campaign — spell yellow + filled dot, same visual cue as the
  // autopilot pill on the detail view so the meaning carries across
  // surfaces. No chip for off-autopilot rows; absence is the default.
  const autopilotChip = campaign.autopilotOn ? (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[var(--ink)] bg-[var(--spell)] px-2.5 py-1 font-mono text-[9.5px] font-bold uppercase tracking-[0.14em] text-[var(--ink)]">
      <span className="text-[9px] leading-none">●</span>
      <Trans>autopilot</Trans>
    </span>
  ) : null;
  // [spec 3a365634] The ask count, as plum as the rest of mag's asks.
  // Rendered BEFORE the status pill so the eye hits "something is waiting"
  // before "it is live" — the founder's complaint was that a live campaign
  // with two open decisions looked exactly like a live campaign with none.
  // Absent, not zero: see CampaignSummary.attentionCount.
  const attention = campaign.attentionCount ?? 0;
  const attentionChip =
    attention > 0 ? (
      <span
        title={t`mag is waiting on you here`}
        className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[var(--plum)] bg-[var(--plum)]/10 px-2.5 py-1 font-mono text-[9.5px] font-bold uppercase tracking-[0.14em] text-[var(--plum)]"
      >
        <span className="text-[9px] leading-none">●</span>
        <Trans>needs you {attention}</Trans>
      </span>
    ) : null;
  const info = (
    <>
      {kindLabel ? (
        <span className="inline-flex shrink-0 items-center rounded-full border border-[var(--ink)]/15 bg-[var(--paper)] px-2.5 py-1 font-mono text-[9.5px] font-bold uppercase tracking-[0.14em] text-[var(--ink)]/75">
          {kindLabel}
        </span>
      ) : null}
      {originChip}
      {autopilotChip}
      <div className="min-w-0 flex-1">
        <p
          className={`truncate font-display text-[17px] font-extrabold leading-tight tracking-[-0.02em] ${
            paused ? 'text-[var(--ink)]/55' : 'text-[var(--ink)]'
          }`}
        >
          {campaign.name ?? campaign.brief.slice(0, 80)}
        </p>
        {campaign.shortPitch ? (
          // s157 — upright serif (was italic) at row size. EB Garamond
          // italic is designed for paragraph reading; at 14px on a
          // single-line row it tightens up and reads cramped. Curly
          // quotes still signal "this is mag's voice" without leaning
          // on italic for the cue.
          <p
            className={`mt-0.5 truncate font-serif text-[14px] leading-snug ${
              paused ? 'text-[var(--ink)]/40' : 'text-[var(--ink)]/65'
            }`}
          >
            “{campaign.shortPitch}”
          </p>
        ) : null}
      </div>
      <span className="flex shrink-0 items-center gap-1.5">
        {attentionChip}
        <span
          className="status-pill inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.08em]"
          style={{ background: pill.bg, color: pill.fg }}
        >
          {pill.label}
          {pill.suffix ? <span className="hidden sm:inline">{pill.suffix}</span> : null}
        </span>
        {secondPill ? (
          // Outlined rather than filled, so the two chips are distinguishable
          // at a glance: the solid one is what google reports, the outlined
          // one is what we measured. A disagreement then reads as two
          // sources, not as a rendering bug.
          <span
            className="status-pill hidden shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.08em] sm:inline-flex"
            style={{ borderColor: 'var(--ink)', background: 'transparent', color: 'var(--ink)' }}
          >
            {secondPill.label}
          </span>
        ) : null}
      </span>
    </>
  );

  // `min-w-0` on the link itself so the inner `truncate` on title +
  // pitch actually fires inside this nested flex (parent's flex-1 alone
  // doesn't constrain children; flex items default to min-width:auto).
  const infoLinkClass = 'flex min-w-0 flex-1 items-center gap-3 no-underline sm:gap-4';
  const linkBody = (() => {
    if (link.kind === 'intake' || link.kind === 'captured') {
      // Both states route back to the brief route — for intake the
      // conversation resumes; for captured it renders read-only so the
      // founder can re-read what was saved.
      return (
        <Link
          to="/brand/$slug/campaigns/new/brief"
          params={{ slug }}
          search={{ campaignId: campaign.id }}
          className={infoLinkClass}
        >
          {info}
        </Link>
      );
    }
    if (link.kind === 'drafting') {
      return (
        <Link
          to="/brand/$slug/campaigns/new/drafting/$id"
          params={{ slug, id: campaign.id }}
          className={infoLinkClass}
        >
          {info}
        </Link>
      );
    }
    if (link.kind === 'review') {
      return (
        <Link
          to="/brand/$slug/campaigns/new/review/$id"
          params={{ slug, id: campaign.id }}
          className={infoLinkClass}
        >
          {info}
        </Link>
      );
    }
    if (link.kind === 'launched') {
      // s135 — launched campaigns open at the details route, not the
      // one-shot success hero. The hero is only for the immediate
      // post-launch moment (founders reach it via the review handoff
      // once per campaign per device).
      return (
        <Link
          to="/brand/$slug/campaigns/$id"
          params={{ slug, id: campaign.id }}
          className={infoLinkClass}
        >
          {info}
        </Link>
      );
    }
    if (link.kind === 'imported') {
      // Unification roadmap Phase 3 — imported campaigns open the
      // takeover view (the dashboard's read of the provider snapshot
      // + mag's analysis + per-row write actions).
      return (
        <Link
          to="/brand/$slug/campaigns/imported/$id"
          params={{ slug, id: campaign.id }}
          className={infoLinkClass}
        >
          {info}
        </Link>
      );
    }
    return <div className={infoLinkClass}>{info}</div>;
  })();

  return (
    // SET ASIDE, NOT DISABLED. A paused row used to be visually identical to a
    // live one — white, same border, same weight — so two stopped campaigns
    // stacked read as a normal list. It now sits on paper with a dashed edge
    // and no lift on hover: the vocabulary this codebase already uses for
    // "nothing is happening here" (see the filter empty state).
    //
    // The CONTROLS are deliberately left at full strength below. Dimming the
    // row is the point; dimming the way out of it would be a different and
    // worse message.
    <article
      className={`group flex items-center gap-2 rounded-xl border-[1.5px] px-4 py-4 transition sm:gap-3 sm:px-5 ${
        paused
          ? 'border-dashed border-[var(--ink)]/25 bg-[var(--paper)] hover:border-[var(--ink)]/45'
          : 'border-[var(--line)] bg-white hover:border-[var(--ink)] hover:shadow-[3px_3px_0_var(--ink)]'
      }`}
    >
      {linkBody}
      {/* NOT hover-gated, and that is the fix rather than a style choice.
          These were `opacity-0 group-hover:opacity-100`, so on a touch device
          there is no hover state and the founder could not reach either one —
          including the control that stops money going out. They are quiet
          (low-contrast until hovered) but always present. */}
      {canPause ? (
        <button
          type="button"
          onClick={onAskPause}
          disabled={busy}
          aria-label={paused ? t`resume campaign` : t`pause campaign`}
          title={paused ? t`resume campaign` : t`pause campaign`}
          className="shrink-0 rounded-md border border-[var(--ink)]/20 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55 transition hover:border-[var(--ink)]/45 hover:text-[var(--ink)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--ink)]/30 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? (
            paused ? (
              <Trans>resuming…</Trans>
            ) : (
              <Trans>pausing…</Trans>
            )
          ) : paused ? (
            <Trans>resume</Trans>
          ) : (
            <Trans>pause</Trans>
          )}
        </button>
      ) : null}
      <button
        type="button"
        onClick={onAskDelete}
        aria-label={t`delete campaign`}
        title={t`delete campaign`}
        className="shrink-0 rounded-md p-1.5 text-[var(--ink)]/40 transition-colors hover:bg-[var(--paper)] hover:text-[var(--pulse)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--ink)]/30"
      >
        <TrashIcon />
      </button>
    </article>
  );
}

function TrashIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

function nextDestination(c: CampaignSummary): {
  kind: 'intake' | 'captured' | 'drafting' | 'review' | 'launched' | 'inert' | 'imported';
} {
  // Unification roadmap Phase 3 — imported campaigns route to the
  // takeover view, not the briefed detail page. They never go
  // through intake/drafting/review states.
  if (c.origin === 'imported') return { kind: 'imported' };
  // Captured campaigns (non-creative kinds with no draft) route back to
  // the intake chat in read-only spirit — the brief screen renders the
  // saved messages so the founder can re-read what they asked for.
  if (c.captured) return { kind: 'captured' };
  if (c.status === 'intake') return { kind: 'intake' };
  if (c.status === 'drafting') return { kind: 'drafting' };
  if (c.status === 'ready') return { kind: 'review' };
  // 'paused' routes to the SAME detail page as 'launched', because that is
  // what it is: a launched campaign that is stopped. Without this clause it
  // fell through to 'inert', which renders a div instead of a Link — so
  // pausing a campaign made it unopenable, and the founder could stop a
  // campaign and then not look at what it did.
  //
  // Third instance of one shape here: a status-keyed branch written while
  // nothing ever wrote 'paused' (isPaused and campaignStatusSchema were the
  // other two). Those were caught by the typechecker. This one was not,
  // because falling through to a default is type-safe and still wrong.
  if (c.status === 'launched' || c.status === 'paused') return { kind: 'launched' };
  return { kind: 'inert' };
}
